import { ColumnDefinition, DataTable } from "@codat/orchard-ui";
import classNames from "classnames";
import { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";

import { sendAmplitudeEvent } from "../../amplitude";
import { CompanySummary } from "../../Models";
import { IRepository } from "../../Repository";
import { opsSettings, SortableColumnKeys } from "../../App";
import CompanyDetailsModal from "../CompanyDetailsModal/CompanyDetailsModal";
import LatestVersionChip from "../LatestVersionChip/LatestVersionChip";
import StatusPill from "../StatusPill/StatusPill";
import CompanyNameCell from "../TableCells/CompanyNameCell/CompanyNameCell";
import IntegrationCell from "../TableCells/IntegrationCell/IntegrationCell";
import LastSeenCell from "../TableCells/LastSeenCell/LastSeenCell";
import TypePill from "../TypePill/TypePill";
import styles from "./CompaniesTable.module.css";

interface Props {
  listItems: CompanySummary[];
  onSort: (column: SortableColumnKeys) => void;
  sortBy: SortableColumnKeys;
  isLatest: (integration: string, version: string) => boolean;
  sortDirection: opsSettings["sort"];
  isLoading: boolean;
  numberOfSkeletonRows: number;
  repo: IRepository;
  token?: string;
}

const CompaniesTable: React.FC<Props> = ({
  listItems,
  onSort,
  sortBy,
  isLatest,
  sortDirection,
  isLoading,
  numberOfSkeletonRows,
  repo,
  token,
}) => {
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<CompanySummary | null>(
    null
  );

  const { pathname } = useLocation();
  const history = useHistory();

  useEffect(() => {
    if (
      pathname !== "/monitor/connector-health" &&
      pathname !== "/" &&
      token &&
      !selectedCompany
    ) {
      const paths = pathname.split("/");
      const companyId = paths[2] || paths[1];

      repo.GetCompanyAsync(companyId, token).then((foundCompany) => {
        if (foundCompany) {
          setSelectedCompany(foundCompany);
          setModalIsOpen(true);
        }
      });
    }
  }, [pathname, repo, token, selectedCompany]);

  const handleOpenModal = (company: CompanySummary) => {
    setSelectedCompany(company);
    history.push(`/monitor/connector-health/${company.companyId}`);
    setModalIsOpen(true);
    sendAmplitudeEvent({
      element: "Connector Modal",
      action: "Click",
    });
  };

  const handleCloseModal = () => {
    history.push("/monitor/connector-health");
    setModalIsOpen(false);
  };

  const columnDefs: ColumnDefinition<
    | "company"
    | "connector/id"
    | "lastSeen"
    | "latestVersion"
    | "type"
    | "syncStatus"
  >[] = [
    {
      key: "company",
      label: "Company",
      component: CompanyNameCell,
      colWidth: 25,
      sortable: true,
    },
    {
      key: "connector/id",
      label: "Connector / ID",
      component: IntegrationCell,
      colWidth: 35,
    },
    {
      key: "lastSeen",
      label: "Last Seen",
      component: LastSeenCell,
      colWidth: 14,
      sortable: true,
    },
    {
      key: "latestVersion",
      label: "Latest Version",
      component: LatestVersionChip,
      colWidth: 12,
      headerAlign: "center",
    },
    {
      key: "type",
      label: "Type",
      component: TypePill,
      colWidth: 10,
    },
    {
      key: "syncStatus",
      label: "Status",
      component: StatusPill,
      colWidth: 10,
      sortable: true,
    },
  ];

  return (
    <>
      <DataTable
        data-testid="companies-table"
        className={classNames(
          isLoading ? styles.skeleton : styles.dataTable,
          styles.hover
        )}
        // @ts-ignore Discrepancy with Orchard types
        columnDefs={isLoading ? [] : columnDefs}
        sortDirection={sortDirection}
        sortKey={sortBy}
        isLayoutAuto
        onSort={onSort}
        isSkeleton={isLoading}
        skeletonRows={numberOfSkeletonRows}
        skeletonRowHeight={70}
        hasBorder
        rowsInfo={listItems.map((item) => {
          return {
            data: [
              {
                key: "company",
                label: "Company",
                value: {
                  companyName: item.companyName,
                  companyId: item.companyId,
                },
              },
              {
                key: "connector/id",
                label: "Connector ID",
                value: {
                  ...item,
                },
              },
              {
                key: "lastSeen",
                label: "Last seen",
                value: {
                  lastSeen: item.lastSeen,
                },
              },
              {
                key: "latestVersion",
                label: "Latest version",
                value: {
                  current: item.connectorVersion,
                  integration: item.connectorIntegration,
                  isLatest,
                },
              },
              {
                key: "type",
                value: {
                  type: item.connectorHostEnvironment,
                },
              },
              {
                key: "syncStatus",
                label: "status",
                value: {
                  syncStatus: item.syncStatus,
                  connectorStatus: item.connectorStatus,
                },
              },
            ],
            onClick: () => handleOpenModal(item),
          };
        })}
      />
      <CompanyDetailsModal
        isOpen={modalIsOpen}
        handleClose={handleCloseModal}
        company={selectedCompany}
        isLatest={isLatest}
      />
    </>
  );
};

export default CompaniesTable;
